/**
 * SegmentCard — single tile in the Marketplace Categories grid.
 *
 * Click → swaps the Categories view to the segment's detail page.
 * Shows: icon, localised name, one-line description, status badge,
 * connector count footer.
 */

import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/cn';
import type { Segment } from '@/data/segments';

export function SegmentCard({
  segment,
  onSelect,
}: {
  segment: Segment;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation('segments');
  const Icon = segment.Icon;
  const ready = segment.status === 'ready';

  return (
    <button
      type="button"
      onClick={() => onSelect(segment.id)}
      data-testid={`segment-card-${segment.id}`}
      data-tier={`tier-${segment.tier}`}
      data-status={segment.status}
      className={cn(
        'group flex h-full flex-col items-start gap-3 rounded-xl border border-border bg-surface p-5 text-start',
        'transition-colors hover:border-border-strong hover:bg-surface-raised',
        'focus-visible:border-spark',
      )}
    >
      <div className="flex w-full items-start justify-between gap-3">
        <span
          className={cn(
            'inline-flex h-10 w-10 items-center justify-center rounded-lg',
            ready ? 'bg-spark-soft text-spark' : 'bg-surface-raised text-ink-muted',
          )}
          aria-hidden
        >
          <Icon className="h-5 w-5" strokeWidth={1.8} />
        </span>
        <StatusBadge status={segment.status} />
      </div>

      <div className="min-w-0">
        <h3 className="text-base font-semibold text-ink">
          {t(`segments.${segment.id}.name`)}
        </h3>
        <p className="mt-1 line-clamp-2 text-sm text-ink-muted">
          {t(`segments.${segment.id}.description`)}
        </p>
      </div>

      <div className="mt-auto flex w-full items-center justify-between pt-2 text-xs text-ink-subtle">
        <span>
          {t('card.connectors_count', { count: segment.connectors.length })}
        </span>
        <span className="font-mono uppercase tracking-wider">
          {t(`tier.t${segment.tier}_label`)}
        </span>
      </div>
    </button>
  );
}

export function StatusBadge({ status }: { status: Segment['status'] }) {
  const { t } = useTranslation('segments');
  const ready = status === 'ready';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
        ready
          ? 'bg-success/10 text-success ring-1 ring-success/30'
          : 'bg-surface-raised text-ink-subtle ring-1 ring-border',
      )}
      data-status={status}
    >
      {t(`status.${ready ? 'ready' : 'coming_soon'}`)}
    </span>
  );
}
